import React, { useContext, useState } from 'react';
import { Button, Divider, Form, Grid, Segment } from 'semantic-ui-react';
import { CustomRangeDateModal, DateRangePowerSelector, DateTimeUtils, LinkedAccount, ModulesFilter, NumberRangePowerSelector, Person, PowerSelector } from 'dyl-components';

import { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import { KEY_ACCOUNT_CO_OWNER, KEY_ACCOUNT_NAME, KEY_ACCOUNT_OWNER, KEY_CREATED, KEY_CREATED_BY, KEY_DUE_ON, KEY_INVOICE_ID, KEY_INVOICE_STATUS, KEY_PAYMENT_TYPE, KEY_PRODUCTS, KEY_SUBTOTAL, KEY_BILLED, KEY_TERM } from 'shared/constants/INVOICE_REPORT_COLUMNS';
import { useSelector } from 'react-redux';
import {  FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getDataNonQuery, getTimestampFilter, useRangeCalendar } from 'shared/CustomDateRange/customDateRange';
import { StringUtils } from 'utils';

const COLUMN_ROWS = [
    [
        { value: KEY_CREATED, label: "Created" },
        { value: KEY_SUBTOTAL, label: "Subtotal" },
    ],
    [
        { value: KEY_CREATED_BY, label: "Created By" },
        { value: KEY_PAYMENT_TYPE, label: "Payment Method" },
    ],
    [
        { value: KEY_DUE_ON, label: "Due On" },
        { value: KEY_TERM, label: "Term" },
    ],
    [
        { value: KEY_INVOICE_ID, label: "Invoice ID", isForcedChecked: true },
        { value: KEY_BILLED, label: "Billed" },
    ],
    [
        { value: KEY_INVOICE_STATUS, label: "Invoice Status" },
        { value: KEY_ACCOUNT_NAME, label: "Account Name" },
    ],
    [
        { value: KEY_ACCOUNT_OWNER, label: "Account Owned By" },
        { value: KEY_ACCOUNT_CO_OWNER, label: "Account Co-Owned By" }
    ],
    [
        { value: KEY_PRODUCTS, label: "Products" },
    ]
];

const INVOICE_FILTER_PARAMS =["created_start_at", "created_end_at", "due_on_start_at", "due_on_end_at", "owner_id", "co_owner_id", "created_by", "account_name", "min_subtotal", "max_subtotal", "payment_type", "invoice_status", "billed", "phone", "terms"]

const InvoicesToolbar = ({ isColumnHidden, onToggleColumn }) => {
    const [selectedInvoices, , areInvoicesInAllPagesSelected] = useContext(BulkActionsContext);

    const hasSelectedQuotesOrOrders = selectedInvoices.length || areInvoicesInAllPagesSelected;

    const [params, setSearchParams] = useSearchParams();

    const [, setIsBulkActionsModalOpen] = useState(false);

    const onOpenBulkActionsModal = () => {
        setIsBulkActionsModalOpen(true);
    }

    const navigate = useNavigate();
    const createdParams = new URLSearchParams();
    const dueOnParams = new URLSearchParams();
    
    if (params?.get("created_start_at")) createdParams.set("start", params.get("created_start_at"));
    if (params?.get("created_end_at")) createdParams.set("end", params.get("created_end_at"));
    
    if (params?.get("due_on_start_at")) dueOnParams.set("start", params.get("due_on_start_at"));
    if (params?.get("due_on_end_at")) dueOnParams.set("end", params.get("due_on_end_at"));
    const {
        dateRange:createdDateRange,
        setCustomDateRangeOpen:setCreatedCustomDateRangeOpen,
        isCustomDateRangeOpen:isCreatedCustomDateRangeOpen,
        setDateRange:setCreatedDateRange,
    } = useRangeCalendar(createdParams);

    const {
        dateRange:dueOnDateRange,
        setCustomDateRangeOpen:setDueOnCustomDateRangeOpen,
        isCustomDateRangeOpen:isDueOnCustomDateRangeOpen,
        setDateRange:setDueOnDateRange,
    } = useRangeCalendar(dueOnParams);


    const location = useLocation();
    const due_on_timestamp = StringUtils.toSnakeCase(getTimestampFilter(params.get("due_on_start_at"), params.get("due_on_end_at"),true) || "All Time");
    const created_timestamp = StringUtils.toSnakeCase(getTimestampFilter(params.get("created_start_at"), params.get("created_end_at"),true) || "All Time");
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            filters: {
                owner_id: (params.get("owner_id")?.split(",") || []).map(id => Number(id)),
                co_owner_id: (params.get("co_owner_id")?.split(",") || []).map(id => Number(id)),
                invoice_status: params.get("invoice_status")?.split(",") || [],
                created_by: (params.get("created_by")?.split(",") || []).map(id => Number(id)),
                account_name: (params.get("account_name")?.split(",") || []).map(id => Number(id)),
                payment_type: params.get("payment_type")?.split(",") || [],
                billed: params.get("billed")?.split(",") || [],
                created: created_timestamp === "custom_range" ? "custom" : created_timestamp,
                due_on: due_on_timestamp === "custom_range" ? "custom" : due_on_timestamp,
                min_subtotal: params.get("min_subtotal"),
                max_subtotal: params.get("max_subtotal"),
                term: (params.get("term")?.split(",") || []).map(id => id)
            },
        }
    });

    const userAuthTimezone = useSelector((state) => state.auth.timezone);
    const formatStatus = (status) => {
            if (!status) {
                return "";
            }
            const statusSplit = status.split("/");
            if (statusSplit[0] !== "rejected") {
                return StringUtils.capitalize(statusSplit[0]);
            }
            const rejectReason = statusSplit[1].split("-");
            return `${StringUtils.capitalize(statusSplit[0])}/${
                rejectReason[0] === "price"
                    ? "Priced"
                    : StringUtils.capitalize(rejectReason[0])
            } ${rejectReason.slice(1, rejectReason.length).join(" ")}`;
        };

    const onSubmit = methods.handleSubmit((data) => {
        const filters = { ...data?.filters };
        if (filters?.created) {
            if (filters.created === "all_time") {
                delete filters.created_start_at;
                delete filters.created_end_at;
                params.delete("created_start_at");
                params.delete("created_end_at");
            } else if (filters.created === "custom") {
                const timestampRange = getDataNonQuery(
                    setCreatedCustomDateRangeOpen,
                    createdDateRange
                );
                if (timestampRange.start && timestampRange.end) {
                    filters.created_start_at = timestampRange.start;
                    filters.created_end_at = timestampRange.end;
                }
            } else {
                const { start, end } = DateTimeUtils.getDateRange(
                    filters.created,
                    DateTimeUtils.getTimezoneCurrentDay(userAuthTimezone),
                    DateTimeUtils.DATE_FORMAT,
                    "day",
                    true,
                    userAuthTimezone
                );
                filters.created_start_at = start;
                filters.created_end_at = end;
            }
            delete filters.created;
        }
        if (filters?.due_on) {
            if (filters.due_on === "all_time") {
                delete filters.due_on_start_at;
                delete filters.due_on_end_at;
                params.delete("due_on_start_at");
                params.delete("due_on_end_at");
            } else if (filters.due_on === "custom") {
                const timestampRange = getDataNonQuery(
                    setDueOnCustomDateRangeOpen,
                    dueOnDateRange
                );
                if (timestampRange.start && timestampRange.end) {
                    filters.due_on_start_at = timestampRange.start;
                    filters.due_on_end_at = timestampRange.end;
                }
            } else {
                const { start, end } = DateTimeUtils.getDateRange(
                    filters.due_on,
                    DateTimeUtils.getTimezoneCurrentDay(userAuthTimezone),
                    DateTimeUtils.DATE_FORMAT,
                    "day",
                    true,
                    userAuthTimezone
                );
                filters.due_on_start_at = start;
                filters.due_on_end_at = end;
            }
            delete filters.due_on;
        }
        if (filters?.term?.length) {
            filters.term = filters.term.filter(val => val !== undefined);
        }
        if (filters?.billed?.includes("subscription")) {
            filters.billed = filters.billed.filter(val => val !== "subscription");
        }
        const existingFiltersObj = Object.fromEntries(params.entries());
        const mergedFilters = { ...existingFiltersObj, ...filters };

        const apiFilters = new URLSearchParams();
        Object.entries(mergedFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value?.length) {
                apiFilters.set(key, value?.join?.(","));
            } else if (
                key === "created_start_at" ||
                key === "created_end_at" ||
                key === "due_on_start_at" ||
                key === "due_on_end_at" ||
                (typeof value === "string"
                    ? value.trim().length > 0
                    : value?.length > 0)
            ) {
                apiFilters.set(key, value);
            }
        });
        apiFilters.set("page", "1");
        setSearchParams(apiFilters);
    });

    const onClearFilter = () => {
        methods.reset();
        setCreatedDateRange("");
        setDueOnDateRange("");

        const columns = params.get("columns");
        const search = params.get("search");
        const phone = params.get("phone");

        const queryParams = new URLSearchParams();

        if (columns) queryParams.set("columns", columns);
        if (search) queryParams.set("search", search);
        if (phone) queryParams.set("phone", phone);
        queryParams.set("page", "1");

        const queryString = queryParams.toString();
        const url = queryString
            ? `${location.pathname}?${queryString}`
            : location.pathname;

        navigate(url);
    };

    const powerSelectors = useSelector(state => (state.invoice_report.powerSelectors));
    console.log("powerSelectors?.co_owners", powerSelectors?.co_owners);
    
    const unassignedOption = { value: 0, text: "Unassigned", key: 0 };
    const ownerOptions = [...(powerSelectors?.owners?.some?.(owner => !owner?.account_id) ? [unassignedOption] : []), ...powerSelectors?.owners?.filter?.(owner => owner?.account_id)?.map(owner => ({
        value: owner.account_id,
        text: owner.username,
        key: owner.account_id,
        content: (<><Person username={owner.username} email={owner.email} isInactiveUser={!owner.active} /></>)
    })) || []];
    const coOwnerOptions = [...(powerSelectors?.co_owners?.some?.(co_owner => !co_owner?.account_id) ? [unassignedOption] : []), ...powerSelectors?.co_owners?.filter?.(co_owner => co_owner?.account_id)?.map(co_owner => ({
        value: co_owner.account_id,
        text: co_owner.username,
        key: co_owner.account_id,
        content: (<><Person username={co_owner.username} email={co_owner.email} isInactiveUser={!co_owner.active} /></>)
    })) || []];
    const createdByOptions = [{text:"System Generated", value:0, key:0, content:(<><Person username={"System Generated"} isSystemGenerated={true} isInactiveUser={false} isUsernameOnly={true}/></>)},...(powerSelectors?.created_by?.map(created_by => ({
        value: created_by.id,
        text: created_by.username,
        key: created_by.id,
        content:(<><Person username={created_by.username} email={created_by.email} isInactiveUser={!created_by.active} /></>)
    })) || [])];
    const accountNameOptions = powerSelectors?.accounts?.map(account => ({
        value: account.id,
        text: account.name,
        key: account.id,
        content:(<> <LinkedAccount account={account.name} subtitle={`ID: ${account.id}`}  /></>)
    })) || [];
    const paymentMethodOptions = powerSelectors?.payment_methods?.map(payment_method => ({
        value: payment_method,
        text: payment_method === "e_check" ? "E-Check" : payment_method?.split("_")?.map(part => StringUtils.capitalize(part)).join(" ") || "",
        key: payment_method
    })) || [];
    const isDyl =  useSelector(state => state.auth.dyl_employee);
    const powerSelectorTerms = powerSelectors?.term || [];
    const netOptions = powerSelectorTerms.filter(term => term);
    const termOptions = isDyl ? [
        {
            label: "Default",
            value: "0",
            key: "default"
        },
        {
            label:"Net",
            value:"net",
            key:"net",
            children:[{
                label:"15",
                value:"15",
                key:"15"
            },{
                label:"30",
                value:"30",
                key:"30"
            },{
                label:"60",
                value:"60",
                key:"60"
            }]
        }
    ] : [
        powerSelectorTerms?.includes(0) ? {
            label: "Default",
            value: "0",
            key: "default"
        } : null,
        netOptions.length ? {
            label: "Net",
            key: "net",
            children: netOptions.map(netOption => ({
                label: String(netOption),
                value: String(netOption),
                key: String(netOption)
            }))
        } : null
    ].filter(Boolean);
    const billedOptions = isDyl ? [{
        text:"Monthly",
        value:"monthly",
        key:"monthly"
    },{
        text:"Quarterly",
        value:"quarterly",
        key:"quarterly"
    },{
        text:"Semi-Annually",
        value:"semi-annually",
        key:"semi_annually"
    },{
        text:"Annually",
        value:"annually",
        key:"annually"
    }] : powerSelectors?.billed?.map(billed => ({
        value: billed,
        text: StringUtils.capitalize(billed),
        key: billed
    })) || [];
    const invoiceStatusOptions = powerSelectors?.invoice_statuses?.map(invoice_status => ({
        value: invoice_status,
        text:  formatStatus(invoice_status),
        key: invoice_status
    })) || [];

    const onDateRangeChange = ({value},isForCreated) => {
        isForCreated ? setCreatedDateRange(value) : setDueOnDateRange(value);
    };

    return (
        <Grid className='InvoicesToolbar'>
            <CustomRangeDateModal
                open={isCreatedCustomDateRangeOpen || isDueOnCustomDateRangeOpen}
                onDateRangeChange={(_,value) => { onDateRangeChange(value,isCreatedCustomDateRangeOpen) }}
                dateRange={isCreatedCustomDateRangeOpen ? createdDateRange : dueOnDateRange}
                getData={() => isCreatedCustomDateRangeOpen ? setCreatedCustomDateRangeOpen(false) : setDueOnCustomDateRangeOpen(false)}
                confirmButtonLabel="Select"
                onClose={() => isCreatedCustomDateRangeOpen ? setCreatedCustomDateRangeOpen(false) : setDueOnCustomDateRangeOpen(false)}
                applyDisabled={
                    isCreatedCustomDateRangeOpen ?
                    !createdDateRange?.split?.(" - ")?.[0] ||
                    !createdDateRange?.split?.(" - ")?.[1] :
                    !dueOnDateRange?.split?.(" - ")?.[0] ||
                    !dueOnDateRange?.split?.(" - ")?.[1]
                }
            />
            <Grid.Row>
                <Grid.Column>
                    <FormProvider {...methods}>
                        <Form onSubmit={onSubmit}>
                            <Segment.Group>
                                <Segment>
                                    <DateRangePowerSelector
                                        label={"Created"}
                                        name={"created"}
                                        onCustomSelect={() =>
                                            setCreatedCustomDateRangeOpen(true)
                                        }
                                        isTimeUnitTogglerVersion
                                    />
                                    <DateRangePowerSelector
                                        label={"Due On"}
                                        name={"due_on"}
                                        onCustomSelect={() =>
                                            setDueOnCustomDateRangeOpen(true)
                                        }
                                        isTimeUnitTogglerVersion
                                    />
                                    <PowerSelector
                                        label={"Account Owned By"}
                                        multiple
                                        name={"owner_id"}
                                        options={ownerOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Account Co-Owned By"}
                                        multiple
                                        name={"co_owner_id"}
                                        options={coOwnerOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Created By"}
                                        multiple
                                        name={"created_by"}
                                        options={createdByOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Account Name"}
                                        multiple
                                        name={"account_name"}
                                        options={accountNameOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <NumberRangePowerSelector
                                        label={"Subtotal"}
                                        name={"subtotal"}
                                        min={1}
                                        isCurrency
                                    />
                                    <PowerSelector
                                        label={"Payment Method"}
                                        multiple
                                        name={"payment_type"}
                                        options={paymentMethodOptions}
                                        className="InvoicesToolbar__Options"
                                        

                                    />
                                    <PowerSelector
                                        label={"Invoice Status"}
                                        multiple
                                        name={"invoice_status"}
                                        options={invoiceStatusOptions}
                                        className="InvoicesToolbar__Options"

                                    />
                                    <PowerSelector
                                        label={"Term"}
                                        multiple
                                        name={"term"}
                                        staggeredCheckbox={true}
                                        allOptionForStaggeredCheckbox={true}
                                        options={termOptions}
                                        className="InvoicesToolbar__Options--scroll"
                                    />
                                    <PowerSelector
                                        label={"Billed"}
                                        multiple
                                        name={"billed"}
                                        options={billedOptions}
                                        className="InvoicesToolbar__Options"
                                    />
                                </Segment>
                                <Segment textAlign="right">
                                    <Button
                                        onClick={onClearFilter}
                                        color="primary"
                                        basic
                                        disabled={
                                            !INVOICE_FILTER_PARAMS.some(
                                                (param) => params.get(param)
                                            )
                                        }
                                    >
                                        Clear Filters
                                    </Button>
                                    <Divider vertical />
                                    <Button
                                        disabled={!methods.formState.isValid || !methods.formState.isDirty}
                                        color="primary"
                                        type="submit"
                                    >
                                        Apply
                                    </Button>
                                </Segment>
                            </Segment.Group>
                        </Form>
                    </FormProvider>
                </Grid.Column>
            </Grid.Row>
            <Grid.Row columns='equal' verticalAlign='middle'>
                <Grid.Column style={{ marginLeft: "-2.75em" }} width={1}>
                    <ModulesFilter
                        hasSearchBar={false}
                        columnRows={COLUMN_ROWS}
                        columnCount={2}
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                    />
                </Grid.Column>
                <Grid.Column />
                <Grid.Column width={4} floated='right'>
                    <Button floated='right' className='InvoicesToolbar__InvoicesButton' disabled={!hasSelectedQuotesOrOrders} onClick={onOpenBulkActionsModal}>
                        Bulk Action
                    </Button>
                </Grid.Column>
            </Grid.Row>
        </Grid>
    )
}

export default InvoicesToolbar;
